import React, { useEffect, useState } from "react";
import ApiURL from "../controllers/Api";
import EmployeeLayout from "./EmployeeLayout";
import { toast } from "../utils/toast";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    return user?.id || 1;
  } catch {
    return 1;
  }
};

const statusOptions = [
  "New",
  "Screening",
  "Interview Scheduled",
  "Interview Completed",
  "Shortlisted",
  "Offer Released",
  "Hired",
];

const statusClasses = {
  New: "bg-slate-100 text-slate-700",
  Screening: "bg-amber-100 text-amber-700",
  "Interview Scheduled": "bg-sky-100 text-sky-700",
  "Interview Completed": "bg-cyan-100 text-cyan-700",
  Shortlisted: "bg-violet-100 text-violet-700",
  "Offer Released": "bg-indigo-100 text-indigo-700",
  Closed: "bg-rose-100 text-rose-700",
  Hired: "bg-green-100 text-green-700",
};

const defaultFollowupForm = {
  lead_id: "",
  type: "call",
  reminder_at: "",
  message: "",
};

const defaultStatusForm = {
  lead_id: "",
  lead_status: "New",
  message: "",
  hired_amount: "",
  hired_amount_unit: "lpa",
};

const convertHiredAmountToLpa = (amount, unit) => {
  if (amount === null || amount === undefined || amount === "") {
    return null;
  }

  return unit === "per_month" ? Number(amount) * 12 / 100000 : Number(amount);
};

const formatHiredAmount = (amount, unit) => {
  const lpaAmount = convertHiredAmountToLpa(amount, unit);
  if (lpaAmount === null) {
    return "-";
  }

  return `₹${lpaAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} LPA`;
};

const IconButton = ({ onClick, title, children, className = "", disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-slate-600 transition hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-slate-600 ${className}`}
  >
    {children}
  </button>
);

const ViewIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8">
    <path d="M1.5 10s3-5 8.5-5 8.5 5 8.5 5-3 5-8.5 5-8.5-5-8.5-5Z" />
    <circle cx="10" cy="10" r="2.5" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8">
    <path d="M3 14.5V17h2.5L15.8 6.7l-2.5-2.5L3 14.5Z" />
    <path d="m11.9 4.8 2.5 2.5" />
  </svg>
);

const FollowupIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8">
    <path d="M10 3.5a6.5 6.5 0 1 0 4.7 11l2.8.8-.8-2.8A6.5 6.5 0 0 0 10 3.5Z" />
    <path d="M7 8.5h6M7 11.5h4" />
  </svg>
);

export default function EmployeeAssignments() {
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState("");
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupSaving, setFollowupSaving] = useState(false);
  const [followupForm, setFollowupForm] = useState(defaultFollowupForm);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusForm, setStatusForm] = useState(defaultStatusForm);
  const [statusTarget, setStatusTarget] = useState(null);

  const loadItems = async () => {
    try {
      const query = new URLSearchParams({ user_id: String(getCurrentUserId()) }).toString();
      const response = await fetch(`${ApiURL}crm/employee/get_my_leads.php?${query}`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load assignments");
      }
      setItems(result?.data || []);
    } catch (error) {
      console.error("Error loading employee assignments:", error);
      setMessage(error.message || "Failed to load assignments.");
      toast.error(error.message || "Failed to load assignments.");
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleStatusChange = async () => {
    if (!statusForm.lead_id) {
      return;
    }

    setStatusSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${ApiURL}crm/employee/update_lead_status.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          lead_id: Number(statusForm.lead_id),
          lead_status: statusForm.lead_status,
          message: statusForm.message,
          hired_amount: statusForm.hired_amount,
          hired_amount_unit: statusForm.hired_amount_unit,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to update status");
      }

      setShowStatusModal(false);
      setStatusTarget(null);
      setStatusForm(defaultStatusForm);
      setMessage(`Status updated to ${statusForm.lead_status}.`);
      toast.success(`Status updated to ${statusForm.lead_status}.`);
      await loadItems();
    } catch (error) {
      setMessage(error.message || "Failed to update status.");
      toast.error(error.message || "Failed to update status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const handleViewResume = (resumeId) => {
    const token = localStorage.getItem("token");
    const url = `${ApiURL}crm/employee/view_assigned_resume.php?resume_id=${resumeId}&token=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openStatusModal = (item) => {
    if (item.is_locked) {
      toast.warning("This deal is already closed after a hire and is locked");
      return;
    }
    setStatusTarget(item);
    setStatusForm({
      lead_id: String(item.id),
      lead_status: item.lead_status || "New",
      message: item.notes || "",
      hired_amount: item.hired_amount ? String(item.hired_amount) : "",
      hired_amount_unit: item.hired_amount_unit || "lpa",
    });
    setShowStatusModal(true);
  };

  const openFollowupModal = (item) => {
    if (item.is_locked) {
      toast.warning("This deal is already closed after a hire and is locked");
      return;
    }
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setFollowupForm({
      lead_id: String(item.id),
      type: "call",
      reminder_at: localDateTime,
      message: "",
    });
    setShowFollowupModal(true);
  };

  const handleCreateFollowup = async () => {
    if (!followupForm.lead_id || !followupForm.message.trim()) {
      setMessage("Please enter a follow-up message.");
      toast.warning("Please enter a follow-up message.");
      return;
    }

    setFollowupSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${ApiURL}crm/employee/create_follow_up.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          lead_id: Number(followupForm.lead_id),
          type: followupForm.type,
          reminder_at: followupForm.reminder_at.replace("T", " ") + ":00",
          message: followupForm.message.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to create follow-up");
      }

      setShowFollowupModal(false);
      setFollowupForm(defaultFollowupForm);
      setMessage("Follow-up created successfully.");
      toast.success("Follow-up created successfully.");
      await loadItems();
    } catch (error) {
      setMessage(error.message || "Failed to create follow-up.");
      toast.error(error.message || "Failed to create follow-up.");
    } finally {
      setFollowupSaving(false);
    }
  };

  return (
    <EmployeeLayout
      title="Assigned Resumes"
      description="See each tagged company requirement separately, update its status, and create follow-ups."
    >
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        {message ? (
          <div className="mb-4 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">
            {message}
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Record</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Company</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Requirement</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Email</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Phone</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Resume</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Current Status</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 ${item.is_hired ? "bg-emerald-50/40" : item.is_locked ? "bg-rose-50/40" : ""}`}>
                  <td className="px-3 py-3 text-slate-900">#{item.id}</td>
                  <td className="px-3 py-3 font-medium text-slate-900">{item.customer_name}</td>
                  <td className="px-3 py-3 text-slate-600">
                    <div>{item.sub_company_name || item.sub_company_names?.join(", ") || "-"}</div>
                    {item.requirement_notes ? (
                      <div className="text-[11px] text-slate-500">{item.requirement_notes}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-slate-600">{item.requirement_name || item.requirement_names?.join(", ") || "-"}</td>
                  <td className="px-3 py-3 text-slate-600">{item.contact_email || "-"}</td>
                  <td className="px-3 py-3 text-slate-600">{item.contact_phone || "-"}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] text-slate-500">{item.original_filename || `Resume #${item.resume_id}`}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[item.lead_status] || "bg-slate-100 text-slate-700"}`}>
                        {item.lead_status || "New"}
                      </span>
                      {item.hired_amount ? (
                        <span className="text-[11px] text-emerald-700">{formatHiredAmount(item.hired_amount, item.hired_amount_unit)}</span>
                      ) : null}
                      {item.is_hired && (item.hired_company_name || item.hired_requirement_name) ? (
                        <span className="text-[11px] text-slate-500">
                          Selected for {item.hired_company_name || "-"}{item.hired_requirement_name ? ` / ${item.hired_requirement_name}` : ""}
                        </span>
                      ) : null}
                      {item.is_locked && !item.is_hired && (item.hired_company_name || item.hired_requirement_name) ? (
                        <span className="text-[11px] text-slate-500">
                          Closed after selection for {item.hired_company_name || "-"}{item.hired_requirement_name ? ` / ${item.hired_requirement_name}` : ""}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <IconButton onClick={() => handleViewResume(item.resume_id)} title="View Resume">
                        <ViewIcon />
                      </IconButton>
                      <IconButton onClick={() => openStatusModal(item)} title={item.is_locked ? "Closed deals are locked" : "Edit Status"} disabled={item.is_locked}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => openFollowupModal(item)} title={item.is_locked ? "Closed deals are locked" : "Add Follow-up"} disabled={item.is_locked}>
                        <FollowupIcon />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                    No assignments found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {showStatusModal && statusTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Edit Status</h3>
                <p className="mt-1 text-xs text-gray-500">
                  {statusTarget.sub_company_name || "-"} / {statusTarget.requirement_name || "-"}
                </p>
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                x
              </button>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-slate-600">
                <div><span className="font-medium text-slate-900">Current Status:</span> {statusTarget.lead_status || "New"}</div>
                {statusTarget.hired_amount ? (
                  <div className="mt-1"><span className="font-medium text-slate-900">Current Hired Amount:</span> {formatHiredAmount(statusTarget.hired_amount, statusTarget.hired_amount_unit)}</div>
                ) : null}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
                <select
                  value={statusForm.lead_status}
                  onChange={(event) =>
                    setStatusForm((prev) => ({
                      ...prev,
                      lead_status: event.target.value,
                      hired_amount: event.target.value === "Hired" ? prev.hired_amount : "",
                      hired_amount_unit: event.target.value === "Hired" ? prev.hired_amount_unit : "lpa",
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {statusForm.lead_status === "Hired" ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Hired Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={statusForm.hired_amount}
                      onChange={(event) =>
                        setStatusForm((prev) => ({ ...prev, hired_amount: event.target.value }))
                      }
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                      placeholder="Enter hired amount in Rs"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Amount Type</label>
                    <select
                      value={statusForm.hired_amount_unit}
                      onChange={(event) =>
                        setStatusForm((prev) => ({ ...prev, hired_amount_unit: event.target.value }))
                      }
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="lpa">LPA</option>
                      <option value="per_month">Per Month</option>
                    </select>
                  </div>
                </div>
              ) : null}

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  rows={4}
                  value={statusForm.message}
                  onChange={(event) =>
                    setStatusForm((prev) => ({ ...prev, message: event.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Add a note for this status update"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusChange}
                disabled={statusSaving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {statusSaving ? "Saving..." : "Save Status"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showFollowupModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Create Follow-up</h3>
                <p className="mt-1 text-xs text-gray-500">Add a follow-up against this assigned requirement.</p>
              </div>
              <button
                onClick={() => setShowFollowupModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                x
              </button>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Type</label>
                <select
                  value={followupForm.type}
                  onChange={(event) => setFollowupForm((prev) => ({ ...prev, type: event.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="call">Call</option>
                  <option value="email">Email</option>
                  <option value="meeting">Meeting</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Reminder Date</label>
                <input
                  type="datetime-local"
                  value={followupForm.reminder_at}
                  onChange={(event) =>
                    setFollowupForm((prev) => ({ ...prev, reminder_at: event.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
                <textarea
                  rows={4}
                  value={followupForm.message}
                  onChange={(event) =>
                    setFollowupForm((prev) => ({ ...prev, message: event.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Write the follow-up note here..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => setShowFollowupModal(false)}
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFollowup}
                disabled={followupSaving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {followupSaving ? "Saving..." : "Save Follow-up"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </EmployeeLayout>
  );
}
